// What Ask AI is told about the routing rules, and its starter questions: one text for the lines
// reports (`backend/src/reports.ts`) and the chat widget's system prompt
// (`frontend/src/features/routing/axai.ts`), so the two cannot drift. The rules themselves are
// the other files here; this only says them in words.

export const WORKLIST_GUIDANCE =
  "source A2000: a line = one WMS # (the app never splits); Deposco: customer + PO (+ DC), SOs as " +
  "SO / PT #, deposco_status MIXED when they differ. Mixed: several customers or dates " +
  "in one WMS, allowed. Review: New, Approved, Updated (changed since approval: see review), Split (pick " +
  "tickets came from other WMS lines), No WMS (a pick ticket with no WMS # yet). Business days skip weekends, 6 " +
  "US holidays and Yom Tov; Route by = CANCEL less the rule, the earliest of the line's pick tickets.";

export const HISTORY_GUIDANCE =
  "Two sources, never merged: App (lines finished in this tool) and Excel 2026 (the old workbook, read-only). " +
  "An Excel finished date is its Schedule Ship Date; marked approx. it is another workbook date (Ready, " +
  "Cancel or Received), not a ship date. The History page shows orders only, no duplicates, finished since " +
  "2026-01-01; do the same unless asked. Regrouped by WMS: a line from before one line per WMS #.";

/** The Worklist's lines, review states and routing window, said once for the chat's system prompt. */
export const ROUTING_DEFINITIONS = [
  "The data: open A2000 pick tickets in warehouses PE, HT and SM only, dated (PT_DATE) 2026-01-01 on; dropship " +
    "customers (DROPSHIP, JCPDROP) are left out. SO / PT # is the pick ticket number; PO is the customer PO " +
    "(A2000 ORDER_NO), always filled; a line may hold several of each, in two separate columns. DC: the " +
    "distribution center, where A2000 has one (Belk, Belk Outlet, Boscov's, Macy's, Bloomingdale's Outlet, " +
    "Walmart USA, Navy Exchange, Marine Corps Exchange); lines are not grouped by DC yet.",
  "A line is one WMS # (A2000 WH_SHIPMENT): every open pick ticket with that WMS #, whatever its customer, " +
    "division, warehouse or dates. Order splitting and merging happen in A2000, never in the app: nobody moves, " +
    "merges or splits lines here. Mixed (a violet badge, its own tab): one WMS holding several customers, " +
    "divisions, warehouses, first ship or cancel dates — allowed (some customers are mixed), information, not " +
    "an error. Mixed lines = mixed Yes, the Mixed tab.",
  "Review: New = a WMS # the app had not seen; Approved = reviewed; Updated = reviewed, then A2000 or Deposco " +
    "changed a pick ticket's First ship, Cancel or Received date (any pick ticket, not only the earliest), QTY or " +
    "PO, or added or removed a pick ticket, or someone changed READY DATE or SCHEDULED SHIP DATE (old → new in " +
    "changes; changed back, Approved again); Split = its pick tickets came from other WMS " +
    "lines, with their entries (all but SCHEDULED SHIP DATE); No WMS = an open pick ticket with no WMS # yet, " +
    "read-only, in its own tab. To review = New + Updated + Split. Clicking New, Updated or Split approves.",
  "Carry-over: when A2000 moves a pick ticket to another WMS, the team's entries come with it. Where the lines " +
    "it came from disagree on a field, nothing wins: the line keeps its own value (or blank) and the field shows " +
    '"2 values" (conflicts) for the team to choose; the 2 values tab (two_values Yes) lists those lines.',
  "Business days skip Saturdays, Sundays, 6 US holidays (New Year's Day, Memorial Day, Independence Day, Labor " +
    "Day, Thanksgiving, Christmas) and Jewish Yom Tov days, Diaspora (Rosh Hashana ×2, Yom Kippur, Sukkot ×2, " +
    "Shmini Atzeret, Simchat Torah, Pesach first 2 and last 2, Shavuot ×2), so Window opens and Route by move " +
    "back past them. Erev days, Chol HaMoed, Chanukah and Purim are business days.",
  "The routing window, per pick ticket from its own customer's two rules: Window opens = FIRST SHIP less rule 1 " +
    "business days; Route by = CANCEL less rule 2 business days. The line takes the earliest of each across its " +
    "pick tickets (the strictest wins), and says which pick ticket set it. Pick tickets without a rule count for " +
    "neither; No routing rule only when none has one. The rule is the app's (Admin → Routing rules, set by a " +
    "manager per customer, one for customers sharing a shown name) when it has one — both days empty there is No " +
    "routing rule on purpose — else A2000's CUST_REF1 / CUST_REF2 (of the customer, or of an A2000 customer with " +
    "the same shown name); a Deposco customer with neither takes the A2000 customer with exactly its name. " +
    "rule_source says where the window came from, in Admin → Routing rules' words, then the days: \"From A2000 · code BEALLS in this group · 10 / 3\" (Set in the app; Set in the app for <code>; From A2000 (this customer); From A2000 · code <code> in this group; From A2000 · same name as <code>; Its own window: kept separate from <name> — a code a manager kept out of its group's window; Turned off in the app; No window). Asked which window a customer uses, give the days and that code.",
  "Window labels: Past deadline (after Route by); Route today (Route by is today); Route by tomorrow; Due in " +
    "2–3 days; Window open (open, not due yet; or open with no deadline); Opens in N days (not open yet); No " +
    "routing rule (no pick ticket has one); Requested · waiting (routing requested); On hold (Need info, Need " +
    "extension, Cancelled); Routed (Routed, Pre-routed).",
  "Appointment date: entered in the app by the routing team, never from A2000. Notes, routed and appointment " +
    "dates live only in the app.",
  "Saved values win over A2000's; A2000's fields (customer, dates, QTY, SO / PT #, PO, WMS, DC) are never " +
    "stored on a line.",
  "Deposco (source Deposco): B2B sales orders (SO #, e.g. SO230792) beside A2000's pick tickets. Every open SO " +
    "is on a line, New ones too (Deposco is only the WMS: M3 allocated it before it reached Deposco). A Deposco line " +
    "is one customer + customer PO; an SO with a DC is a line of customer + PO + DC once Deposco gives it a " +
    "group — until then it is not a line: it waits for a group (counted apart, deposco_waiting). Deposco status " +
    "(read-only): New < Released < Back Ordered < Wave Built < Picking < Pick Complete < Partially Staged < Staged < Partially Shipped " +
    "< Ship Complete; the line shows its SOs' highest, with MIXED when they differ (Canceled apart). DLIX, CO and " +
    "Group are Deposco's numbers. Its tabs: Gone from Deposco (every SO left Deposco's open orders, not marked " +
    "shipped here) and Shipped in Deposco (a flag: Ship Complete or a ship date). No WMS, Gone from A2000 and " +
    "Shipped in A2000 are A2000 only. Routing rule: as A2000's (above); Deposco has no CUST_REF1/2.",
];

export const WORKLIST_SUGGESTIONS = [
  "How many lines are past deadline?",
  "Deposco lines with a MIXED status",
  "Lines to review at PE",
  "Not routed lines by customer",
  "Lines with no routing rule",
  "Lines with Work Needed set",
];

export const HISTORY_SUGGESTIONS = [
  "Belk shipped since August",
  "Shipped last month by customer",
  "Which shipped dates are approximate?",
  "Cancelled lines since August",
];

/** The Shipping page: its schedule and Ship log, said once for Ask AI's system prompt. */
export const SHIPPING_GUIDANCE =
  "Shipping schedule: lines (one per WMS #, or a Deposco customer + PO) the team put on a pickup — one truck with a date, App Time, carrier, " +
  "door and PICK UP # / SHIPMENT # / BOL #, typed by the team (nothing from A2000) — grouped by pickup date; a " +
  "past date still open is late. Ship log: lines the team marked shipped in this app, by ship date, with " +
  "tracking and who shipped them; the old workbook's shipments are in History only. Shipped in A2000: open lines " +
  "whose pick tickets the WMS has SHIPPED or BILLED — a flag only; nobody marked them shipped here yet.";

export const SHIPPING_SUGGESTIONS = [
  "What ships tomorrow?",
  "What did FedEx take last week?",
  "Lines shipped in A2000 but not marked",
];

/** Every page, said once: the chat panel and each page's prompt know them all, whichever page is open. */
export const APP_PAGES =
  "The app's pages: Worklist (open lines to review, route and produce), Pre-route (A2000 orders routed before " +
  "their pick ticket exists; they move to the Worklist by themselves), Shipping (the Shipping schedule: lines on " +
  "pickups; the Ship log: lines marked shipped), History (finished lines, read-only), System log (every change " +
  "people made: when, who, which line, what changed; Restore) and Admin (managers: Divisions, Customers, Routing " +
  "rules, Customer assignments).";

/** The System log, said once for Ask AI's system prompt. */
export const SYSTEM_LOG_GUIDANCE =
  "Each entry is one save a person made in the app, newest first: when, who, the line (customer · PTs · PO), the " +
  "action and what changed (old → new). Actions: Edited (a field typed), Approved, Chose a value (2 values), " +
  "Scheduled a pickup, Added to a pickup, Changed a pickup, Taken off a pickup, Shipped (marked by hand: Mark " +
  "shipped), Finished, Restored, Changed a list, Pre-route, Reviewed, Workbook import. Only people's changes: " +
  "A2000 and Deposco changes are not entries. Restore puts a value back on the page itself.";

export const SYSTEM_LOG_SUGGESTIONS = [
  "Changes today",
  "Who approved lines this week",
  "Lines shipped by hand last month",
];

export const PRE_ROUTE_SUGGESTIONS = [
  "How many pre-routes alert?",
  "Pre-routes waiting to move",
  "Which pre-routes are late?",
];

/** Admin's tabs, said once for Ask AI's system prompt. */
export const ADMIN_GUIDANCE =
  "Admin (managers): Divisions — the abbreviation every page shows for a division (default: the name's first 7 " +
  "characters). Customers — the name every page shows for a customer (several may share one; default: A2000 its " +
  "code, Deposco its name). Routing rules — each customer's routing window (business days before First ship / " +
  "Cancel): set in the app, from A2000's CUST_REF1/2, turned off, or none; customers sharing a shown name are one " +
  "row and share the strictest window. Customer assignments — who looks after which customers, Owner or Backup, " +
  "the codes each label covers; Needs confirming: labels with no linked code; No owner: customers with open lines " +
  "and no Owner.";

/** Admin's bar chips, per tab, from its placeholder's family (Import has no bar). */
export const ADMIN_SUGGESTIONS = {
  "routing-rules": ["Customers with no window", "Set in the app", "Groups with the strictest window"],
  assignments: ["Customers with no owner", "Needs confirming", "Who looks after Belk?"],
  customers: ["Codes sharing a shown name", "Named, by source"],
  divisions: ["Abbreviations changed"],
} as const;
